/**
 * Which inventory hub tabs NTX actually sees — the policy, with no dependency
 * on the module barrel so it stays unit-testable (the barrel pulls MUI icons,
 * which don't resolve under vitest's node environment).
 *
 * The module ships 24 tabs. Grant's objection on the 2026-08-06 walkthrough was
 * that most of them don't map to NTX's operation — "there's like a bunch of tabs
 * here if you don't use like more than half of them" — and he asked for a
 * simpler product to look at. The agreed scope is raw materials (rock/sand/
 * cement), finished products, and a single mix design.
 *
 * **Reorder Suggestions** was hidden in the first pass and brought back on
 * 2026-08-12. Its priority counts used to read 0/0/0 because of an upstream
 * demand-query bug; that was fixed backend-side (now 9 late / 5 critical /
 * 4 urgent), and knowing what to reorder is the payoff of tracking raw
 * materials at all — which is the part of the scope Grant actually endorsed
 * ("mainly, like rock, sand, cement, like you have it").
 *
 * See meetings/2026-08-06_ntx-inventory/frontend-implementation-plan.md (F.1).
 */

/** The hub opens on this tab, so it must stay visible or the strip has no selection. */
export const DEFAULT_TAB_ID = "inventory-stock";

export const NTX_VISIBLE_TAB_IDS: readonly string[] = [
  "inventory-dashboard", // the one-screen overview Grant reacted well to
  "inventory-raw-materials", // rock / sand / cement — the confirmed scope
  "inventory-products", // finished products (standard vs non-standard, F.3)
  "inventory-product-units", // the serialized piece in the yard
  "inventory-lots", // receipts + mill certs behind the raw materials
  "inventory-mix-designs", // NTX runs a single mix (F.5)
  DEFAULT_TAB_ID, // Stock Levels — on-hand + the ledger
  "inventory-reorder", // what to buy — the payoff of tracking raw materials
  "inventory-reports", // the export Grant asked about
];

/**
 * Hidden for now, and why — kept as a record so the next person doesn't have to
 * re-derive it from the meeting notes:
 *
 * - Hardware, Categories, Locations ... catalog plumbing, not day-to-day screens
 * - Bills of Materials .............. no BOM process at NTX today
 * - Work Orders ..................... pending Grant: does NTX cast against work
 *                                     orders, or straight off the production
 *                                     schedule? (F.11 — flip this back if so)
 * - Production Batches .............. batch plant isn't integrated
 * - Quality ......................... overlaps the QC module; QC isn't
 *                                     registered on NTX yet (F.10)
 * - Reservations, Transfers,
 *   Cycle Counts, Dispatches ........ out of the agreed scope
 * - Raw-Material Orders ............. manual PO entry is exactly what Grant
 *                                     objected to; waits on the accounting
 *                                     integration (Eric)
 * - Scan ............................ no camera capture exists yet (F.9)
 * - Identifiers ..................... admin surface for the above
 * - Audit Log ....................... internal diagnostics
 */
const VISIBLE = new Set(NTX_VISIBLE_TAB_IDS);

export const isInventoryTabVisible = (tabId: string): boolean => VISIBLE.has(tabId);
